import {observer} from 'mobx-react-lite';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  InputAdornment
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {TitleSummaryTemplate} from '../../../../entity/model/title-summary-template';
import {TitleSummaryTemplateDialogProps} from '../../../../entity/props/report/title-summary-template-dialog-props';
import {TitleSummaryTemplateDto} from '../../../../entity/dto/title-summary-template-dto';
import {ManageMode} from '../../../../entity/enums/manage-mode';
import {useService} from '../../../../infrastructure/service/service';
import {useState, useEffect} from 'react';
import LoadingComponent from '../widget/LoadingComponent';

import '../../../../assets/css/style.css';
import '../../../../assets/css/reports/title-summary-template-dialog.css';

const TitleSummaryTemplateDialog = (props: TitleSummaryTemplateDialogProps): JSX.Element => {
  const {titleSummaryTemplateService, userService} = useService();
  const [sectionsAccordion, setSectionsAccordion] = useState<boolean>(true);
  const [filteredTemplates, setFilteredTemplates] = useState<TitleSummaryTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);

  const mode = props.mode ?? ManageMode.EXPORT;

  useEffect(() => {
    if (mode === ManageMode.IMPORT && props.open)
      loadTemplates();
  }, [mode, props.open]);

  const loadTemplates = async (): Promise<void> => {
    setIsLoadingTemplates(true);

    const user = await userService.getCurrentUser();
    if (user?.organizationID) {
      const templatesData = await titleSummaryTemplateService.getTitleSummaryTemplates(user.organizationID, false);
      setFilteredTemplates(templatesData);
    }

    setIsLoadingTemplates(false);
  }

  const handleTemplateSelect = (template: TitleSummaryTemplate): void => {
    if (props.onTemplateSelect)
      props.onTemplateSelect(template);
        
    props.onClose();
  }

  const handleTemplateCancel = (): void => {
    props.onClose();
  }

  const handleValidate = (values: TitleSummaryTemplateDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.templateName.trim())
      errors.templateName = 'Template name is required.';

    const hasSelectedSections = Object.values(values).slice(1).some(Boolean);
    if (!hasSelectedSections)
      errors.sectionSelection = 'At least one section must be selected.';

    return errors;
  }

  const handleTemplateSubmit = async (values: TitleSummaryTemplateDto, {setSubmitting}: FormikHelpers<TitleSummaryTemplateDto>): Promise<void> => {
    if (!values.templateName.trim() || Object.values(values).slice(1).every((v) => !v)) {
      setSubmitting(false);
      return;
    }

    const currentUser = await userService.getCurrentUser();
    const template: TitleSummaryTemplate = {
      organizationID: currentUser?.organizationID ?? '',
      templateName: values.templateName.trim(),
      createdByUserID: props.titleSummaryRequest.createdByUserID,
      comment: values.includeGeneralComments ? props.titleSummaryRequest.comment : undefined,
      orderInformation: values.includeOrderInfo ? props.titleSummaryRequest.orderInformation : undefined,
      propertyInformation: values.includePropertyInfo ? props.titleSummaryRequest.propertyInformation : undefined,
      vestingDeeds: values.includeVestingDeed ? props.titleSummaryRequest.vestingDeeds : undefined,
      estates: values.includeEstates ? props.titleSummaryRequest.estates : undefined,
      taxYears: values.includeTaxes ? props.titleSummaryRequest.taxYears : undefined,
      taxFlags: values.includeTaxes ? props.titleSummaryRequest.taxFlags : undefined,
      mobileHome: values.includeMobileHome ? props.titleSummaryRequest.mobileHome : undefined,
      mortgages: values.includeMortgages ? props.titleSummaryRequest.mortgages : undefined,
      judgments: values.includeJudgments ? props.titleSummaryRequest.judgments : undefined,
      liens: values.includeLiens ? props.titleSummaryRequest.liens : undefined,
      uccs: values.includeUCCs ? props.titleSummaryRequest.uccs : undefined,
      restrictionsAgreements: values.includeRestrictions ? props.titleSummaryRequest.restrictionsAgreements : undefined,
      easementsRightOfWays: values.includeEasements ? props.titleSummaryRequest.easementsRightOfWays : undefined
    };

    await titleSummaryTemplateService.createTitleSummaryTemplate(currentUser?.organizationID ?? '', template);
    props.onClose();
    setSubmitting(false);
  }

  const handleSelectAll = (handleChange: any): void => {
    handleChange({target: {name: 'includeOrderInfo', value: true}});
    handleChange({target: {name: 'includePropertyInfo', value: true}});
    handleChange({target: {name: 'includeOwnerInfo', value: true}});
    handleChange({target: {name: 'includeVestingDeed', value: true}});
    handleChange({target: {name: 'includeTaxes', value: true}});
    handleChange({target: {name: 'includeMortgages', value: true}});
    handleChange({target: {name: 'includeEstates', value: true}});
    handleChange({target: {name: 'includeJudgments', value: true}});
    handleChange({target: {name: 'includeLiens', value: true}});
    handleChange({target: {name: 'includeUCCs', value: true}});
    handleChange({target: {name: 'includeRestrictions', value: true}});
    handleChange({target: {name: 'includeEasements', value: true}});
    handleChange({target: {name: 'includeGeneralComments', value: true}});
    handleChange({target: {name: 'includeMobileHome', value: true}});
  }

  const handleClearAll = (handleChange: any): void => {
    handleChange({target: {name: 'includeOrderInfo', value: false}});
    handleChange({target: {name: 'includePropertyInfo', value: false}});
    handleChange({target: {name: 'includeOwnerInfo', value: false}});
    handleChange({target: {name: 'includeVestingDeed', value: false}});
    handleChange({target: {name: 'includeTaxes', value: false}});
    handleChange({target: {name: 'includeMortgages', value: false}});
    handleChange({target: {name: 'includeEstates', value: false}});
    handleChange({target: {name: 'includeJudgments', value: false}});
    handleChange({target: {name: 'includeLiens', value: false}});
    handleChange({target: {name: 'includeUCCs', value: false}});
    handleChange({target: {name: 'includeRestrictions', value: false}});
    handleChange({target: {name: 'includeEasements', value: false}});
    handleChange({target: {name: 'includeGeneralComments', value: false}});
    handleChange({target: {name: 'includeMobileHome', value: false}});
  }

  const handleSelectAllChange = (handleChange: any) => (event: any): void => {
    if (event.target.checked)
      handleSelectAll(handleChange);
    else
      handleClearAll(handleChange);
  }

  const renderExportMode = (): JSX.Element => (
    <Formik
      initialValues={titleSummaryTemplateService.getTitleSummaryTemplateDto()}
      enableReinitialize={true}
      validate={handleValidate}
      onSubmit={handleTemplateSubmit}>
      {({values, errors, touched, handleChange, handleBlur, isSubmitting}: FormikProps<TitleSummaryTemplateDto>): JSX.Element => {
        const allSelected = Object.values(values).slice(1).every(Boolean);

        return (
          <Form>
            <DialogContent dividers>
              <Paper elevation={0}>
                <Box className='title-summary-template-name-section'>
                  <TextField
                    fullWidth
                    required
                    name='templateName'
                    id='input-template-name'
                    label='Template Name'
                    value={values.templateName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder='e.g., Standard Residential, Commercial Property, etc.'
                    autoFocus
                    error={touched.templateName && Boolean(errors.templateName)}
                    helperText={touched.templateName && errors.templateName}
                    className='zitles-textfield'/>
                </Box>
                <Accordion
                  elevation={0}
                  className='template-dialog-card'
                  expanded={sectionsAccordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => setSectionsAccordion(!sectionsAccordion)} className='dialog-icon'/>}>
                    <Typography className='template-data-value accordion'>Sections</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box className='checkbox-template-container header template-checkbox'>
                      <Checkbox
                        id='checkbox-select-all'
                        checked={allSelected}
                        onChange={handleSelectAllChange(handleChange)}/>
                      <Typography className='checkbox-template-container title'>
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </Typography>
                    </Box>
                    {
                      (errors as any).sectionSelection &&
                      <Typography variant='body2' color='error' className='zitles-form-error'>
                        {(errors as any).sectionSelection}
                      </Typography>
                    }
                    <Grid container spacing={3} className='title-summary-template-sections-grid'>
                      <Grid size={6}>
                        <Box className='title-summary-template-section-column'>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-order-info'
                              name='includeOrderInfo'
                              checked={values.includeOrderInfo}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Order Information</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-property-info'
                              name='includePropertyInfo'
                              checked={values.includePropertyInfo}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Property Information</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-owner-info'
                              name='includeOwnerInfo'
                              checked={values.includeOwnerInfo}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Owner Information</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-vesting-deed'
                              name='includeVestingDeed'
                              checked={values.includeVestingDeed}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Vesting Deed</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-taxes'
                              name='includeTaxes'
                              checked={values.includeTaxes}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Taxes</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-mortgages'
                              name='includeMortgages'
                              checked={values.includeMortgages}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Mortgages</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-estates'
                              name='includeEstates'
                              checked={values.includeEstates}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Estates</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid size={6}>
                        <Box className='title-summary-template-section-column'>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-judgments'
                              name='includeJudgments'
                              checked={values.includeJudgments}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Judgments</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-liens'
                              name='includeLiens'
                              checked={values.includeLiens}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Liens</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-uccs'
                              name='includeUCCs'
                              checked={values.includeUCCs}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>UCCs</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-restrictions'
                              name='includeRestrictions'
                              checked={values.includeRestrictions}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Restrictions / Agreements</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-easements'
                              name='includeEasements'
                              checked={values.includeEasements}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Easements & Right of Ways</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-general-comments'
                              name='includeGeneralComments'
                              checked={values.includeGeneralComments}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>General Comments</Typography>
                          </Box>
                          <Box className='checkbox-template-container template-checkbox'>
                            <Checkbox
                              id='checkbox-mobile-home'
                              name='includeMobileHome'
                              checked={values.includeMobileHome}
                              onChange={handleChange}/>
                            <Typography className='checkbox-template-container title'>Mobile Home</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            </DialogContent>
            <DialogActions className='action-btn-container'>
              <Button
                id='btn-template-cancel'
                type='button'
                variant='outlined'
                onClick={handleTemplateCancel}
                className='zitles-button cancel'>
                Cancel
              </Button>
              <Button
                id='btn-template-submit'
                type='submit'
                disabled={!values.templateName.trim() || Object.values(values).slice(1).every((v) => !v) || isSubmitting}
                className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
                startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
                {isSubmitting ? 'Creating Template' : 'Submit'}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const renderImportMode = (): JSX.Element => {
    return (
      <Formik
        initialValues={{ selectedTemplate: null as TitleSummaryTemplate | null }}
        onSubmit={() => {}}>
        {({ values, setFieldValue }: FormikProps<{ selectedTemplate: TitleSummaryTemplate | null }>): JSX.Element => (
          <Form>
            <DialogContent dividers>
              <Box className='space-y-4 py-4'>
                <Autocomplete
                  options={filteredTemplates}
                  getOptionLabel={(option: TitleSummaryTemplate): string => option.templateName}
                  value={values.selectedTemplate}
                  onChange={(event, newValue) => {
                    setFieldValue('selectedTemplate', newValue);
                    if (newValue) {
                      handleTemplateSelect(newValue);
                    }
                  }}
                  renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                    <TextField
                      {...params}
                      id='input-template-search'
                      placeholder='Type to search templates...'
                      className='zitles-textfield'
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position='start'>
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                  noOptionsText='No Template Found'
                  loading={isLoadingTemplates}
                  loadingText='Loading templates...'/>
              </Box>
            </DialogContent>
            <DialogActions className='action-btn-container'>
              <Button
                id='btn-template-cancel-import'
                type='button'
                variant='outlined'
                onClick={handleTemplateCancel}
                className='zitles-button cancel'>
                Cancel
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <Dialog
      open={props.open}
      onClose={handleTemplateCancel}
      maxWidth='md'
      fullWidth
      className='zitles-dialog'>
      <DialogTitle className='zitles-dialog-title'>
        <span>{mode === ManageMode.EXPORT ? 'Export Title Summary Template' : 'Import Title Summary Template'}</span>
        <IconButton id='btn-dialog-close' onClick={handleTemplateCancel}>
          <CloseIcon className='dialog-icon'/>
        </IconButton>
      </DialogTitle>
      {
        isLoadingTemplates &&
          <LoadingComponent />
      }

      {
        mode === ManageMode.EXPORT
          ? renderExportMode()
          : renderImportMode()
      }
    </Dialog>
  );
}

export default observer(TitleSummaryTemplateDialog);
